/**
 * Ações de uma mensagem (reagir, apagar, transcrever, encaminhar), ajustes
 * rápidos e a reunião gravada virando CRM.
 *
 * Um pedaço do objeto de ações do `Provedor` (`estado.jsx`), cortado por área.
 * Recebe o contexto uma vez, dentro do `useMemo` de lá: a identidade de cada
 * ação continua estável, como era no objeto único.
 */
import * as api from "../api.js";

export function acoesDaMensagem(c) {
  const { avisar, avisarSeFoiEnsaio, carregarCaso, casoAberto, mudar, recarregarCaso, ref, tentar } = c;
  return {
    reagirMensagem: (mid, emoji) =>
      tentar("mensagem", async () => {
        await api.reagir(casoAberto(), mid, emoji);
        await recarregarCaso();
      }),

    /* guarda-ok: pergunta — a pergunta está no menu da mensagem
       (`AcoesDaMensagem.jsx`), onde o item fica ao lado de "copiar". */
    apagarMensagem: (mid) =>
      tentar("mensagem", async () => {
        await api.apagarMensagem(casoAberto(), mid);
        await recarregarCaso();
        avisar("Mensagem apagada para todos.");
      }),

    /**
     * Manda de novo a mensagem que ficou em ensaio.
     *
     * Sem confirmação: o gesto JÁ é a confirmação — o botão só existe na bolha
     * que não saiu, e diz "Enviar agora". `avisarSeFoiEnsaio` cobre o caso de
     * o WhatsApp ainda estar fora, e aí a bolha continua onde estava.
     */
    reenviarEnsaio: (mid) =>
      tentar("mensagem", async () => {
        const r = await api.reenviarEnsaio(casoAberto(), mid);
        await recarregarCaso();
        if (r?.via === "ensaio") avisarSeFoiEnsaio(r);
        else avisar("Mensagem enviada ao cliente.");
      }),

    /**
     * Ler por máquina a mídia desta bolha.
     *
     * O que chega e o que sai já nasce transcrito; o botão existe para o que
     * ficou para trás — conversa importada, serviço fora do ar na hora,
     * cota estourada. Recarrega o caso porque o texto lido vira parte da
     * bolha, da busca e do que o copiloto enxerga.
     */
    /* Ocupado com o id: com "mensagem" seco, ler UM áudio (ou reagir, editar)
       acendia "LENDO…" em toda mídia sem transcrição da conversa. */
    transcreverMidia: (mid) =>
      tentar(`midia:${mid}`, async () => {
        await api.transcreverMensagem(casoAberto(), mid);
        await recarregarCaso();
      }),

    /**
     * A IA assume a conversa para buscar o que falta na peça.
     *
     * Devolve `true` quando o pedido entrou: é o que faz o cartão do convite
     * sumir da coluna dos fatos sem esperar releitura — e some porque o pedido
     * agora está na fila de Automação, não porque o dado chegou.
     */
    pedirOQueFalta: async (id) => {
      const ok = await tentar("falta", async () => {
        const r = await api.pedirOQueFalta(id);
        await recarregarCaso();
        avisar(`A IA vai perguntar ao cliente. A primeira pergunta está na fila de Automação, esperando aprovação.`);
        return r;
      });
      return Boolean(ok);
    },

    encaminharMensagens: (mensagens, para) =>
      tentar("mensagem", async () => {
        const r = await api.encaminhar(casoAberto(), mensagens, para);
        await recarregarCaso();
        const falhas = (r?.falhas ?? []).length;
        avisar(
          falhas ? `${r.enviadas ?? 0} encaminhadas, ${falhas} falharam.` : `${r?.enviadas ?? mensagens.length} encaminhadas.`,
        );
      }),

    /** Vale por si: "digitando…" que falha não pode virar aviso vermelho. */
    avisarDigitando: (parou = false, como = "digitando") => {
      const id = casoAberto();
      if (id) api.digitando(id, parou, como);
    },

    alternarAjuste: (chave) =>
      tentar("ajuste", async () => {
        const r = await api.ajustar(chave, ref.current.ajustes[chave] === "1" ? "0" : "1");
        mudar({ ajustes: r.ajustes });
      }),

    escolherMotor: (motor) =>
      tentar("motor", async () => {
        const r = await api.ajustar("copiloto_motor", motor);
        mudar({ ajustes: r.ajustes, motor: await api.motor() });
      }),

    /*
     * A reunião gravada, subindo.
     *
     * Aqui só entra o que é do CRM: a qual caso ela pertence, recarregar a
     * conversa para o cartão aparecer na trilha e refazer a lista de reuniões.
     * Quem grava é `useGravacaoReuniao` — as duas trilhas, a pausa, o medidor e
     * os quadros da tela moram lá, e não em quinze linhas de MediaRecorder
     * duplicadas aqui.
     */
    guardarReuniao: async (gravada, { config, titulo } = {}) => {
      const casoId = casoAberto();
      const cliente = ref.current.caso?.registro?.nome?.trim();
      /* Duas palavras bastam no RÓTULO da fala: o nome completo do cliente
         ("SEBASTIÃO ALVES DO CARMO") aparece em cada linha da transcrição e
         empurra a fala para baixo do próprio nome. O nome inteiro continua na
         ficha, que é onde ele é documento. */
      const comoChamar = (nome) => nome?.split(/\s+/).slice(0, 2).join(" ") ?? "";
      return tentar("reuniao", async () => {
        const salva = await api.guardarReuniao({
          ...gravada,
          /*
           * Quem falou tem NOME, e ele já está aqui: a sessão sabe quem conduziu
           * e a ficha sabe quem é o cliente. A transcrição saía com "Escritório"
           * e "Cliente" — rótulo de ferramenta —, e é esse texto que vira citação
           * na petição e resumo no e-mail. O rótulo dos ajustes só entra quando
           * não há caso aberto para dizer o nome.
           */
          config: {
            ...config,
            rotuloMic: comoChamar(ref.current.usuario?.nome) || config?.rotuloMic || "",
            rotuloOutroLado: comoChamar(cliente) || config?.rotuloOutroLado || "",
          },
          titulo: titulo || (cliente ? `Reunião com ${cliente}` : "Reunião"),
          recordId: casoId ?? null,
        });
        avisar(`Reunião transcrita: ${salva.palavras ?? 0} palavra(s).`);
        if (config?.resumirAoTerminar) await api.resumirGravacao(salva.gravacao.id).catch(() => {});
        if (casoId) await carregarCaso(casoId, { comCopiloto: false });
        return salva;
      });
    },

    /**
     * Um momento marcado da reunião virando prova do caso.
     *
     * Recebe o texto de quem o tem na mão (o marcador da gravação salva). A
     * versão anterior ia procurar o trecho num `trechos` que só existia durante
     * a gravação: clicar no momento de uma reunião ENCERRADA não fazia nada.
     */
    citarMomento: async ({ segundo, texto }) => {
      if (!casoAberto()) return avisar("Abra um atendimento para citar o trecho.");
      const emTempo = `${Math.floor(segundo / 60)}:${String(segundo % 60).padStart(2, "0")}`;
      return tentar("prova", async () => {
        await api.anotarProva({
          record_id: casoAberto(),
          rotulo: (texto || `momento ${emTempo}`).slice(0, 40),
          detalhe: texto || `Momento marcado na reunião, em ${emTempo}.`,
          especie: "Prova",
          origem: "gravacao",
          segundo,
          fonte: `reunião ${emTempo}`,
        });
        avisar("Trecho marcado como prova.");
      });
    },
  };
}
